import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { bs } from '../../../styles/styles';
import { InputFieldError } from '../../../components/InputFieldError';
import {
	ISimpleAutoCompleteSearchFieldEvent,
	ISimpleAutoCompleteSearchFieldItemSelectionEvent,
	SimpleAutoCompleteSearchField,
} from '../../../components/autocomplete/SimpleAutoCompleteSearchField';
import { styleSheet } from '../../../components/automation/AutomationStepCard/styles';

interface TagStepFormProps {
	/**
	 * The current tag name value.
	 */
	tagName: string;
	/**
	 * Whether this is an "Add tag" step (vs "Remove tag").
	 */
	isAddTag: boolean;
	/**
	 * Whether the form can be edited.
	 */
	canEdit: boolean;
	/**
	 * Error message to display for validation.
	 */
	errorMessage: string | null;
	/**
	 * Unique key for the form (used for React key prop).
	 */
	baseKey: string;
	/**
	 * Callback when tag name changes (on blur or selection).
	 */
	onTagChange: (newTagName: string) => void;
	/**
	 * Callback when save should be triggered (on blur).
	 */
	onSave: (newTagName: string) => void;
	/**
	 * Callback to clear error messages.
	 */
	onClearError: () => void;
}

/**
 * Form component for editing tag automation step content.
 * Displays a tag autocomplete search field with validation.
 */
export function TagStepForm({
	tagName,
	isAddTag: _isAddTag,
	canEdit,
	errorMessage,
	baseKey,
	onTagChange,
	onSave,
	onClearError,
}: TagStepFormProps) {
	const [selectedTag, setSelectedTag] = React.useState<string>(tagName || '');

	// Sync internal state with prop when it changes externally
	React.useEffect(() => {
		setSelectedTag(tagName || '');
	}, [tagName]);

	const onTagSearchFieldChanged = (e: ISimpleAutoCompleteSearchFieldEvent<React.ChangeEvent<HTMLInputElement>>) => {
		if (e.sourceEvent) {
			const inputElement = e.sourceEvent.target as HTMLInputElement;
			const newValue = inputElement.value || '';
			setSelectedTag(newValue);
			onTagChange(newValue);
		}
	};

	const onTagSearchClearButtonClicked = () => {
		setSelectedTag('');
		onTagChange('');
		onSave('');
	};

	const onSearchTagSelected = (
		e: ISimpleAutoCompleteSearchFieldItemSelectionEvent<string | Api.IEntity | Api.IUser>
	) => {
		if (!e.selection || typeof e.selection !== 'string') {
			return;
		}

		if (e.target) {
			e.target.setSearchQuery(e.selection);
		}
		const newSelectedTag = e.selection;
		setSelectedTag(newSelectedTag);
		onTagChange(newSelectedTag);
		onSave(newSelectedTag);
	};

	const onTagSearchFieldKeyDown = (e: ISimpleAutoCompleteSearchFieldEvent<React.KeyboardEvent<HTMLInputElement>>) => {
		if (!!e.sourceEvent && e.sourceEvent.keyCode === 13) {
			const inputElement = e.sourceEvent.target as HTMLInputElement;
			inputElement.blur();
		}
	};

	const onBlur = () => {
		onSave(selectedTag);
	};

	return (
		<>
			<div className={css(styleSheet.fieldLabel)}>
				Select a tag: &nbsp;
				<span className={css(bs.required)}>*</span>
			</div>
			<InputFieldError errorMessage={errorMessage}>
				<SimpleAutoCompleteSearchField
					disabled={!canEdit}
					initialSearchQuery={selectedTag || undefined}
					key={`${baseKey}-tag-select`}
					onBlur={onBlur}
					onChange={onTagSearchFieldChanged}
					onClear={onTagSearchClearButtonClicked}
					onFocus={onClearError}
					onItemSelected={onSearchTagSelected}
					onKeyDown={onTagSearchFieldKeyDown}
					resultsLimit={10}
					pageSize={10}
					placeholder='Search or add a new tag'
					renderNoResultsItem={false}
					style={styleSheet.tagSearchField}
					type={Api.ResourceAutoCompleteViewModelType.Tag}
				/>
			</InputFieldError>
		</>
	);
}
